// src/pages/operations-dashboard/components/ShipmentsOverviewSection.jsx
import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { Package, Truck, Clock, CreditCard, Banknote } from "lucide-react";
import {
  formatCompact,
  formatPercent,
  formatShortDate,
  CHART_COLORS,
} from "../../../utils/dashboardConstants";

const SectionCard = ({ title, subtitle, icon: Icon, children, className = "" }) => (
  <div className={`bg-white rounded-2xl border border-gray-100 overflow-hidden ${className}`}>
    <div className="px-6 py-4 border-b border-gray-50">
      <div className="flex items-center gap-3">
        {Icon && (
          <div className="p-2 rounded-xl bg-blue-50">
            <Icon className="w-4.5 h-4.5 text-blue-500" strokeWidth={2} />
          </div>
        )}
        <div>
          <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
          {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
        </div>
      </div>
    </div>
    <div className="p-6">{children}</div>
  </div>
);

const StatPill = ({ label, value, color, icon: Icon }) => (
  <div className="flex items-center gap-3 p-3.5 bg-gray-50/70 rounded-xl hover:bg-gray-50 transition-colors">
    <div className={`p-2 rounded-lg ${color}`}>
      {Icon && <Icon className="w-4 h-4 text-white" strokeWidth={2.5} />}
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-xs text-gray-500 font-medium truncate">{label}</p>
      <p className="text-lg font-bold text-gray-900 tracking-tight">{value}</p>
    </div>
  </div>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white/95 backdrop-blur-lg border border-gray-100 rounded-xl shadow-2xl shadow-gray-200/50 px-4 py-3">
      <p className="text-xs font-semibold text-gray-500 mb-1.5">{formatShortDate(label)}</p>
      {payload.map((entry, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
          <span className="text-sm font-medium text-gray-800">{formatCompact(entry.value)}</span>
          <span className="text-xs text-gray-400">shipments</span>
        </div>
      ))}
    </div>
  );
};

const Skeleton = ({ className = "" }) => (
  <div className={`bg-gray-100 rounded-lg animate-pulse ${className}`} />
);

export default function ShipmentsOverviewSection({ data, loading }) {
  const isLoading = loading?.shipmentsOverview;

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 p-6">
          <Skeleton className="h-6 w-48 mb-6" />
          <Skeleton className="h-64" />
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
          <Skeleton className="h-6 w-32 mb-4" />
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-16" />
          ))}
        </div>
      </div>
    );
  }

  if (!data) return null;

  const dailyData = (data.daily_shipment_counts || []).map((d) => ({
    date: d.date,
    count: d.count,
  }));

  const prepaidData = [
    { name: "Prepaid", value: data.prepaid_percentage || 0 },
    { name: "COD", value: data.cod_percentage || 0 },
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* Daily Shipment Trend */}
      <SectionCard
        title="Daily Shipment Trend"
        subtitle="Shipments created per day"
        icon={Package}
        className="lg:col-span-2"
      >
        {dailyData.length > 0 ? (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={dailyData} margin={{ top: 5, right: 5, bottom: 5, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis
                dataKey="date"
                tickFormatter={formatShortDate}
                tick={{ fontSize: 11, fill: "#94a3b8" }}
                axisLine={{ stroke: "#e2e8f0" }}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 11, fill: "#94a3b8" }}
                axisLine={false}
                tickLine={false}
                tickFormatter={formatCompact}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: "#f1f5f9", radius: 6 }} />
              <Bar
                dataKey="count"
                fill="#3b82f6"
                radius={[6, 6, 0, 0]}
                maxBarSize={40}
              />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-64 flex items-center justify-center text-gray-400 text-sm">
            No shipment data available for the selected period
          </div>
        )}
      </SectionCard>

      {/* Shipment Summary Stats */}
      <div className="space-y-4">
        <SectionCard title="Shipment Split" subtitle="Prepaid vs COD" icon={CreditCard}>
          <div className="flex items-center gap-4">
            <div className="w-28 h-28 flex-shrink-0">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={prepaidData}
                    cx="50%"
                    cy="50%"
                    innerRadius={30}
                    outerRadius={50}
                    paddingAngle={3}
                    dataKey="value"
                    startAngle={90}
                    endAngle={-270}
                  >
                    <Cell fill="#3b82f6" />
                    <Cell fill="#f59e0b" />
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-2.5 flex-1">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full bg-blue-500" />
                  <span className="text-xs text-gray-600 font-medium">Prepaid</span>
                </div>
                <span className="text-sm font-bold text-gray-900">{formatPercent(data.prepaid_percentage)}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full bg-amber-500" />
                  <span className="text-xs text-gray-600 font-medium">COD</span>
                </div>
                <span className="text-sm font-bold text-gray-900">{formatPercent(data.cod_percentage)}</span>
              </div>
            </div>
          </div>
        </SectionCard>

        <div className="grid grid-cols-1 gap-3">
          <StatPill
            label="Total Forward"
            value={formatCompact(data.total_forward_shipments || 0)}
            color="bg-blue-500"
            icon={Truck}
          />
          <StatPill
            label="Picked Up"
            value={formatCompact(data.picked_shipments || 0)}
            color="bg-emerald-500"
            icon={Package}
          />
          <StatPill
            label="Pending Pickup"
            value={formatCompact(data.pending_pickup_shipments || 0)}
            color="bg-amber-500"
            icon={Clock}
          />
        </div>
      </div>
    </div>
  );
}
